import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { useLocation } from '@reach/router';
import { graphql, Link } from 'gatsby';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { LoadingIndicator, Text, TextVariant } from 'hem-ui';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import React, { Suspense } from 'react';
import getSlug from 'speakingurl';

import Office from '../Office';
import { RichTextPressQuote } from '../RichTextPressQuote';
import { blockquote, root } from './RichText.module.scss';
import RichTextAsset from './RichTextAsset';
import RichTextButton from './RichTextButton';
import RichTextCenteredText from './RichTextCenteredText';
import RichTextGallery from './RichTextGallery';
import RichTextGrid from './RichTextGrid';
import RichTextHero from './RichTextHero';
import RichTextLinkGroup from './RichTextLinkGroup';
import RichTextNewsletterSignupForm from './RichTextNewsletterSignupForm';
import RichTextPageTitle from './RichTextPageTitle';
import RichTextProductGrid from './RichTextProductGrid';
import { RichTextReview } from './RichTextReview';

const RichTextProfile = React.lazy(() => import('./RichTextProfile'));
const RichTextVideo = React.lazy(() => import('./RichTextVideo'));
const RichTextTypeform = React.lazy(() => import('./RichTextTypeform'));

export const isExternalSlug = (link: string) =>
  link.startsWith('http') ||
  link.startsWith('mailto') ||
  link.startsWith('tel');

export const fixSlug = (string: string) => {
  if (!string) {
    return undefined;
  }

  if (isExternalSlug(string)) {
    return string;
  }

  let str = string.toLowerCase();
  // ensure string starts with slash
  str = str.startsWith('/') ? str : '/' + str;
  // remove trailing slash
  str = str.endsWith('/') ? str.slice(0, -1) : str;

  return str;
};

export const RichText: React.FC<{
  content: Queries.RichTextContentFragment;
}> = ({ content }): JSX.Element => {
  const location = useLocation();

  return (
    <div className={root}>
      {
        // @ts-expect-error rich text types are hard
        renderRichText(content, {
          renderText: (text) => {
            return text.split('\n').reduce((children, textSegment, index) => {
              return [
                ...children,
                index > 0 && <br key={index} />,
                textSegment,
              ];
            }, []);
          },
          renderMark: {
            [MARKS.BOLD]: (children) => <strong>{children}</strong>,
            [MARKS.ITALIC]: (children) => <em>{children}</em>,
            [MARKS.UNDERLINE]: (children) => <u>{children}</u>,
            [MARKS.CODE]: (children) => <code>{children}</code>,
          },
          renderNode: {
            [INLINES.HYPERLINK]: (node, children) => {
              if (isString(node?.data?.uri) && !!children) {
                return <a href={node.data.uri}>{children}</a>;
              }

              console.warn('[INLINES.HYPERLINK] Not rendered', {
                node,
                children,
                pathname: location.pathname,
              });
            },
            [INLINES.ENTRY_HYPERLINK]: (node, children) => {
              const entry = node.data.target;
              if (entry) {
                if (entry.__typename === 'ContentfulPageProduct') {
                  if (!entry.defaultVariant?.slug) {
                    return children;
                  }
                  return (
                    <Link to={fixSlug(entry.defaultVariant.slug)}>
                      {children}
                    </Link>
                  );
                }
                return <Link to={fixSlug(entry.slug)}>{children}</Link>;
              }

              console.warn('[INLINES.ENTRY_HYPERLINK] Not rendered', {
                node,
                children,
                pathname: location.pathname,
              });
            },
            [INLINES.ASSET_HYPERLINK]: (node, children) => {
              const entry = node.data.target;

              if (entry?.__typename === 'ContentfulAsset') {
                return <a href={entry.file.url}>{children}</a>;
              }

              console.warn('[INLINES.ASSET_HYPERLINK] Not rendered', {
                node,
                children,
                pathname: location.pathname,
              });
            },
            [INLINES.EMBEDDED_ENTRY]: (node, children) => {
              console.warn('EMBEDDED_ENTRY not rendered', { node, children });
            },
            [BLOCKS.HEADING_1]: (node, children) => (
              <Text
                tag="h1"
                variant={TextVariant.HEADLINE_SECTION}
                id={
                  node?.content?.[0]?.nodeType === 'text' &&
                  isString(node?.content?.[0]?.value)
                    ? getSlug(node.content[0].value, { truncate: 20 })
                    : undefined
                }
              >
                {children}
              </Text>
            ),
            [BLOCKS.HEADING_2]: (node, children) => {
              return (
                <Text
                  tag="h2"
                  variant={TextVariant.HEADLINE_SECTION}
                  id={
                    node?.content?.[0]?.nodeType === 'text' &&
                    isString(node?.content?.[0]?.value)
                      ? getSlug(node.content[0].value, { truncate: 20 })
                      : undefined
                  }
                >
                  {children}
                </Text>
              );
            },
            [BLOCKS.HEADING_3]: (node, children) => (
              <Text
                tag="h3"
                variant={TextVariant.TITLE}
                id={
                  node?.content?.[0]?.nodeType === 'text' &&
                  isString(node?.content?.[0]?.value)
                    ? getSlug(node.content[0].value, { truncate: 20 })
                    : undefined
                }
              >
                {children}
              </Text>
            ),
            [BLOCKS.HEADING_4]: (node, children) => (
              <Text
                tag="h4"
                variant={TextVariant.TITLE}
                id={
                  node?.content?.[0]?.nodeType === 'text' &&
                  isString(node?.content?.[0]?.value)
                    ? getSlug(node.content[0].value, { truncate: 20 })
                    : undefined
                }
              >
                {children}
              </Text>
            ),
            [BLOCKS.PARAGRAPH]: (_node, children) => {
              if (isString(children) && children !== '') {
                return (
                  <Text tag="p" variant={TextVariant.BODY_SMALL}>
                    {children}
                  </Text>
                );
              }

              if (
                isArray(children) &&
                children.filter((child) => child !== '').length > 0
              ) {
                return (
                  <Text tag="p" variant={TextVariant.BODY_SMALL}>
                    {children}
                  </Text>
                );
              }

              return null;
            },
            [BLOCKS.EMBEDDED_ENTRY]: (node) => {
              switch (node?.data?.target?.__typename) {
                case 'ContentfulComponentTypeform': {
                  const entry = get(node, 'data.target');
                  return (
                    <Suspense fallback={<LoadingIndicator />}>
                      <RichTextTypeform {...entry} />
                    </Suspense>
                  );
                }
                case 'ContentfulYotpoProductReview': {
                  const entry = get(node, 'data.target');
                  return <RichTextReview {...entry} placement="richtext" />;
                }
                case 'ContentfulComponentProfile': {
                  const entry = get(node, 'data.target');

                  if (!entry) {
                    console.warn('[RichTextProfile] Entry not rendered', {
                      node,
                      typename: node?.data?.target?.__typename,
                      pathname: location.pathname,
                    });

                    return null;
                  }

                  return (
                    <Suspense fallback={<LoadingIndicator />}>
                      <RichTextProfile
                        {...(entry as Queries.RichTextProfileFragment)}
                      />
                    </Suspense>
                  );
                }
                case 'ContentfulOffice': {
                  const entry = get(node, 'data.target');

                  if (!entry) {
                    console.warn('[RichTextOffice] Entry not rendered', {
                      node,
                      typename: node?.data?.target?.__typename,
                      pathname: location.pathname,
                    });

                    return null;
                  }

                  return <Office {...(entry as Queries.OfficeFragment)} />;
                }
                case 'ContentfulComponentPressQuote':
                  // @ts-expect-error rich text types are hard
                  return <RichTextPressQuote {...{ node }} />;
                case 'ContentfulComponentMarketingSignupForm':
                  // @ts-expect-error rich text types are hard
                  return <RichTextNewsletterSignupForm {...{ node }} />;
                case 'ContentfulComponentLinkGroup':
                  // @ts-expect-error rich text types are hard
                  return <RichTextLinkGroup {...{ node }} />;
                case 'ContentfulComponentVideo':
                  return (
                    <Suspense fallback={<LoadingIndicator />}>
                      {/* @ts-expect-error rich text types are hard */}
                      <RichTextVideo {...{ node }} />
                    </Suspense>
                  );
                case 'ContentfulComponentGallery':
                  // @ts-expect-error rich text types are hard
                  return <RichTextGallery {...{ node }} />;
                case 'ContentfulComponentHero':
                  // @ts-expect-error rich text types are hard
                  return <RichTextHero {...{ node }} />;
                case 'ContentfulComponentCenteredText':
                  // @ts-expect-error rich text types are hard
                  return <RichTextCenteredText {...{ node }} />;
                case 'ContentfulComponentGrid':
                  // @ts-expect-error rich text types are hard
                  return <RichTextGrid {...{ node }} />;
                case 'ContentfulComponentButton':
                  // @ts-expect-error rich text types are hard
                  return <RichTextButton {...{ node }} />;
                case 'ContentfulComponentProductGrid':
                  // @ts-expect-error rich text types are hard
                  return <RichTextProductGrid {...{ node }} />;
                case 'ContentfulComponentPageTitle':
                  // @ts-expect-error rich text types are hard
                  return <RichTextPageTitle {...{ node }} />;
                default:
                  console.warn('[BLOCKS.EMBEDDED_ENTRY] Entry not rendered', {
                    node,
                    typename: node?.data?.target?.__typename,
                    pathname: location.pathname,
                  });
                  return null;
              }
            },
            [BLOCKS.QUOTE]: (node) => {
              const content: string[] = [];
              node.content.forEach((item) => {
                if (item.nodeType !== 'paragraph') return null;
                item.content.forEach((item) => {
                  if (item.nodeType !== 'text') return null;
                  if (item.value.length < 1) return null;
                  content.push(item.value);
                });
              });

              return (
                <Text
                  tag="blockquote"
                  variant={TextVariant.HEADLINE_SMALL}
                  className={blockquote}
                >
                  {content.map((text) => {
                    // if the text first char is —, return <cite>text</cite>
                    if (text[0] === '—') {
                      return <cite className="not-italic">{text}</cite>;
                    }

                    // if the text first char is “, make it a hanging quote on large screens
                    if (text[0] === '“') {
                      return <p className="md:indent-[-0.4em]">{text}</p>;
                    }

                    return <p>{text}</p>;
                  })}
                </Text>
              );
            },
            [BLOCKS.EMBEDDED_ASSET]: (node) => {
              switch (node?.data?.target?.__typename) {
                case 'ContentfulAsset':
                  // @ts-expect-error rich text types are hard
                  return <RichTextAsset {...{ node }} />;
                default:
                  console.warn('[BLOCKS.EMBEDDED_ASSET] Entry not rendered', {
                    node,
                    typename: node?.data?.target?.__typename,
                    pathname: location.pathname,
                  });
                  return null;
              }
            },
          },
        })
      }
    </div>
  );
};

export const query = graphql`
  fragment RichTextContent on ContentfulPageContent {
    raw
    references {
      ... on ContentfulComponentProfile {
        ...RichTextProfile
      }

      ... on ContentfulAsset {
        ...RichTextAsset
      }

      ... on ContentfulComponentGallery {
        ...RichTextGallery
      }

      ... on ContentfulComponentProductGrid {
        ...RichTextProductGrid
      }

      ... on ContentfulComponentVideo {
        ...RichTextVideo
      }

      ... on ContentfulPageDesigner {
        __typename
        contentful_id
        slug
      }

      ... on ContentfulPage {
        __typename
        contentful_id
        slug
      }

      ... on ContentfulPageProduct {
        __typename
        contentful_id
        defaultVariant {
          slug
        }
      }

      ... on ContentfulComponentPressQuote {
        ...RichTextPressQuote
      }

      ... on ContentfulComponentButton {
        ...RichTextButton
      }

      ... on ContentfulComponentCenteredText {
        ...RichTextCenteredText
      }

      ... on ContentfulComponentPageTitle {
        ...RichTextPageTitle
      }

      ... on ContentfulComponentMarketingSignupForm {
        ...RichTextNewsletterSignupForm
      }

      ... on ContentfulOffice {
        __typename
        contentful_id
        ...Office
      }

      ... on ContentfulComponentHero {
        ...RichTextHero
      }

      ... on ContentfulComponentGrid {
        ...RichTextGrid
      }

      ... on ContentfulComponentLinkGroup {
        ...RichTextLinkGroup
      }

      ... on ContentfulComponentTypeform {
        ...RichTextTypeform
      }

      ... on ContentfulYotpoProductReview {
        __typename
        contentful_id
        ...ProductReview
      }
    }
  }
`;
